import type { User } from "oidc-client-ts";
import type {
  ActiveReviewTasksResponse,
  ConfigStatus,
  PipelineVariablesResponse,
  DashboardStats,
  ActivityItem,
  Developer,
  DeveloperDetail,
  DeveloperListResponse,
  DeveloperProfileUpdateRequest,
  DeveloperAssessmentResponse,
  DevelopmentConfigResponse,
  DevelopmentCostResponse,
  DevelopmentPollResponse,
  DevelopmentReliabilityResponse,
  DevelopmentStatsResponse,
  DevelopmentTrendResponse,
  DiagnosticsResponse,
  ReviewHistoryItem,
  ReviewTriggerResponse,
  ReviewTaskStatus,
  MRReview,
  ReleaseDetailResponse,
  ReleaseResponse,
  BulkContextUpdateResponse,
  ContextPushGuidelineResponse,
  ContextPushHistoryResponse,
  ContextPushPreviewResponse,
  ContextPushTriggerResponse,
  ContextUpdateApproveRequest,
  ContextUpdateDetailResponse,
  ContextUpdateResponse,
  CIFailureAnalysisResponse,
  EngineEventResponse,
  GitLabMergeRequest,
  GitLabProject,
  Guideline,
  GuidelineCreateRequest,
  GuidelineUpdateRequest,
  KiraSettings,
  PaginatedResponse,
  QualityConfigStatusResponse,
  QualityDashboardResponse,
  QualityPromptAnalytics,
  ReviewQualityResponse,
  EpicContextResponse,
  EpicSiblingResponse,
  FailureSignatureTaxonomyResponse,
  FalsePositiveConfirmResponse,
  FalsePositiveDismissResponse,
  FalsePositiveListResponse,
  TicketAssessmentDetailResponse,
  TicketAssessmentResponse,
  PipelineFlowResponse,
  TicketProgressResponse,
  ProjectContext,
  ProjectContextCoverageItem,
  ProjectDeveloper,
  ProjectReviewSettings,
  PushContextToRepoResponse,
  ShepherdResponse,
  TeamInfo,
  JiraDiagnosticsResponse,
  SlackDiagnosticsResponse,
  WorkerStatusResponse,
} from "./types";

import { getApiBaseUrl, getApiRootUrl } from "./apiBase";

const BASE_URL = getApiBaseUrl();
const ROOT_URL = getApiRootUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function statusMessage(status: number): string {
  if (status === 401) return "Authentication required. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested resource was not found.";
  if (status === 422) return "The request contained invalid data.";
  if (status === 429) return "Too many requests. Please try again later.";
  if (status >= 500) return "A server error occurred. Please try again later.";
  return `Request failed (${status}).`;
}

let getUser: (() => User | null | undefined) | null = null;

export function setAuthUserProvider(provider: () => User | null | undefined) {
  getUser = provider;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getUser?.()?.access_token;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((options.headers as Record<string, string>) ?? {}),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (!response.ok) {
    throw new ApiError(response.status, statusMessage(response.status));
  }

  return response.json();
}

export const api = {
  // Dashboard
  getDashboardStats: () => request<DashboardStats>("/dashboard/stats"),
  getRecentActivity: (limit = 20) => request<ActivityItem[]>(`/dashboard/activity?limit=${limit}`),

  // Developers
  getDevelopers: (page = 1, pageSize = 25, search?: string, team?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set("search", search);
    if (team) params.set("team", team);
    return request<DeveloperListResponse>(`/developers?${params}`);
  },
  getDeveloper: (id: number) => request<DeveloperDetail>(`/developers/${id}`),
  deleteDeveloper: (id: number) =>
    request<{ status: string; developer_id: number }>(`/developers/${id}`, {
      method: "DELETE",
    }),
  updateDeveloperProfile: (id: number, data: DeveloperProfileUpdateRequest) =>
    request<Developer>(`/developers/${id}/profile`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
  getTeams: () => request<TeamInfo[]>("/developers/teams"),

  // Reviews
  getReviews: (page = 1, pageSize = 25, developerId?: number) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (developerId) params.set("developer_id", String(developerId));
    return request<PaginatedResponse<ReviewHistoryItem>>(`/reviews?${params}`);
  },

  // Context updates
  getContextUpdates: (page = 1, pageSize = 25, status?: string) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (status) params.set("status", status);
    return request<PaginatedResponse<ContextUpdateResponse>>(`/context-updates?${params}`);
  },
  getContextUpdate: (id: number) =>
    request<ContextUpdateDetailResponse>(`/context-updates/${id}`),
  approveContextUpdate: (id: number, body: ContextUpdateApproveRequest) =>
    request<ContextUpdateResponse>(`/context-updates/${id}/approve`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  dismissContextUpdate: (id: number) =>
    request<ContextUpdateResponse>(`/context-updates/${id}/dismiss`, {
      method: "POST",
    }),
  bulkApproveContextUpdates: (ids: number[]) =>
    request<BulkContextUpdateResponse>("/context-updates/bulk-approve", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  bulkDismissContextUpdates: (ids: number[]) =>
    request<BulkContextUpdateResponse>("/context-updates/bulk-dismiss", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  revertContextUpdate: (id: number) =>
    request<ContextUpdateResponse>(`/context-updates/${id}/revert`, {
      method: "POST",
    }),

  // Guidelines
  getGuidelines: (scope?: string, projectPath?: string) => {
    const params = new URLSearchParams();
    if (scope) params.set("scope", scope);
    if (projectPath) params.set("project_path", projectPath);
    const qs = params.toString();
    return request<Guideline[]>(`/guidelines${qs ? `?${qs}` : ""}`);
  },
  getGuideline: (id: number) => request<Guideline>(`/guidelines/${id}`),
  createGuideline: (body: GuidelineCreateRequest) =>
    request<Guideline>("/guidelines", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateGuideline: (id: number, body: GuidelineUpdateRequest) =>
    request<Guideline>(`/guidelines/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteGuideline: (id: number) =>
    request<{ status: string }>(`/guidelines/${id}`, {
      method: "DELETE",
    }),

  // GitLab browsing
  getGitLabProjects: (search?: string, page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set("search", search);
    return request<PaginatedResponse<GitLabProject>>(`/gitlab/projects?${params}`);
  },
  getGitLabMergeRequests: (
    opts: {
      project?: string;
      group?: string;
      state?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ) => {
    const params = new URLSearchParams({
      page: String(opts.page ?? 1),
      page_size: String(opts.pageSize ?? 25),
      state: opts.state ?? "opened",
    });
    if (opts.project) params.set("project", opts.project);
    if (opts.group) params.set("group", opts.group);
    return request<PaginatedResponse<GitLabMergeRequest>>(`/gitlab/merge-requests?${params}`);
  },

  // Config status
  getConfigStatus: () => request<ConfigStatus>("/config-status"),
  getPipelineVariables: () =>
    request<PipelineVariablesResponse>("/pipeline-variables"),

  // Settings
  getSettings: () => request<KiraSettings>("/settings"),
  getCursorModels: () => request<string[]>("/settings/cursor-models"),
  updateSettings: (settings: Partial<KiraSettings>) =>
    request<KiraSettings>("/settings", {
      method: "PATCH",
      body: JSON.stringify(settings),
    }),
  resetModelRouting: () =>
    request<KiraSettings>("/settings/model-routing", {
      method: "DELETE",
    }),

  // Review triggers
  triggerReview: (body: {
    project_id: number;
    project_path: string;
    mr_iid: number;
    mr_url: string;
    author_username: string;
    author_name: string;
    post_to_gitlab?: boolean;
    model?: string;
  }) =>
    request<ReviewTriggerResponse>("/reviews/trigger", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  getReviewTaskStatus: (taskId: string) =>
    request<ReviewTaskStatus>(`/reviews/task-status/${taskId}`),
  getReviewsByMr: (projectPath: string, mrIid: number) => {
    const params = new URLSearchParams({
      project_path: projectPath,
      mr_iid: String(mrIid),
    });
    return request<{ reviews: MRReview[] }>(`/reviews/by-mr?${params}`);
  },
  getActiveReviewTasks: (mrKeys: string[]) => {
    const params = new URLSearchParams();
    for (const key of mrKeys) {
      params.append("mr_keys", key);
    }
    return request<ActiveReviewTasksResponse>(`/reviews/active-tasks?${params}`);
  },

  // Developer assessment
  getDeveloperAssessment: (developerId: number) =>
    request<DeveloperAssessmentResponse>(`/developers/${developerId}/assessment`),

  // Project contexts
  getProjectContexts: (search?: string, page = 1, pageSize = 25) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set("search", search);
    return request<PaginatedResponse<ProjectContext>>(`/projects/contexts?${params}`);
  },
  getProjectContext: (projectPath: string) =>
    request<ProjectContext>(`/projects/contexts/${encodeURIComponent(projectPath)}`),
  saveProjectContext: (
    projectPath: string,
    body: {
      business_context: string;
      platform: string;
      language_and_frameworks: string;
      libraries: string;
      gotchas: string;
      additional_notes: string;
    },
  ) =>
    request<ProjectContext>(`/projects/contexts/${encodeURIComponent(projectPath)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteProjectContext: (projectPath: string) =>
    request<{ status: string }>(`/projects/contexts/${encodeURIComponent(projectPath)}`, {
      method: "DELETE",
    }),

  // Project context coverage
  getProjectContextCoverage: (search?: string, page = 1, pageSize = 25) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set("search", search);
    return request<PaginatedResponse<ProjectContextCoverageItem>>(`/projects/contexts/coverage?${params}`);
  },
  pushContextToRepo: (projectPath: string) =>
    request<PushContextToRepoResponse>(`/projects/contexts/${encodeURIComponent(projectPath)}/push-to-repo`, {
      method: "POST",
    }),

  // Project review settings
  getProjectReviewSettings: (search?: string, page = 1, pageSize = 20) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (search) params.set("search", search);
    return request<PaginatedResponse<ProjectReviewSettings>>(`/projects/review-settings?${params}`);
  },
  updateProjectReviewSettings: (
    projectPath: string,
    body: {
      auto_review_enabled: boolean;
      context_learning_enabled: boolean;
      pipeline_analysis_enabled: boolean;
      post_to_gitlab_enabled: boolean;
    },
  ) =>
    request<ProjectReviewSettings>(`/projects/review-settings/${encodeURIComponent(projectPath)}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  // Project developers
  getProjectDevelopers: (projectPath: string) => {
    const params = new URLSearchParams({ project_path: projectPath });
    return request<{ items: ProjectDeveloper[]; total: number }>(`/projects/developers?${params}`);
  },

  // Shepherd
  getShepherd: () => request<ShepherdResponse>("/shepherd"),
  setShepherd: (developerId: number) =>
    request<ShepherdResponse>("/shepherd", {
      method: "PUT",
      body: JSON.stringify({ developer_id: developerId }),
    }),
  getShepherdHistory: (page = 1, pageSize = 25) =>
    request<PaginatedResponse<ShepherdResponse>>(`/shepherd/history?page=${page}&page_size=${pageSize}`),

  // Worker status
  getWorkerStatus: () => request<WorkerStatusResponse>("/workers/status"),

  // Operations
  getOperationsReleases: (page = 1, pageSize = 20) => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
    });
    return request<ReleaseResponse[]>(`/operations/releases?${params}`);
  },
  getOperationsRelease: (releaseId: number) =>
    request<ReleaseDetailResponse>(`/operations/releases/${releaseId}`),
  getOperationsCIFailures: (opts?: { page?: number; pageSize?: number; projectPath?: string }) => {
    const params = new URLSearchParams({
      page: String(opts?.page ?? 1),
      page_size: String(opts?.pageSize ?? 20),
    });
    if (opts?.projectPath) params.set("project_path", opts.projectPath);
    return request<CIFailureAnalysisResponse[]>(`/operations/ci-failures?${params}`);
  },
  getOperationsEvents: (opts?: {
    page?: number;
    pageSize?: number;
    source?: string;
    eventType?: string;
  }) => {
    const params = new URLSearchParams({
      page: String(opts?.page ?? 1),
      page_size: String(opts?.pageSize ?? 50),
    });
    if (opts?.source) params.set("source", opts.source);
    if (opts?.eventType) params.set("event_type", opts.eventType);
    return request<EngineEventResponse[]>(`/operations/events?${params}`);
  },

  // Quality (Phase 7)
  getQualityDashboard: (days = 30) => {
    const params = new URLSearchParams({ days: String(days) });
    return request<QualityDashboardResponse>(`/quality/dashboard?${params}`);
  },
  getReviewQuality: (days = 30, projectPath?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (projectPath) params.set("project_path", projectPath);
    return request<ReviewQualityResponse>(`/quality/review-quality?${params}`);
  },
  getDevelopmentReliability: (days: 7 | 30 | 90 = 30, projectPath?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (projectPath) params.set("project_path", projectPath);
    return request<DevelopmentReliabilityResponse>(
      `/quality/development-reliability?${params}`,
    );
  },
  getQualityProjects: () => request<string[]>("/quality/projects"),
  getPromptAnalytics: (days = 30, projectPath?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (projectPath) params.set("project_path", projectPath);
    return request<QualityPromptAnalytics>(`/quality/prompt-analytics?${params}`);
  },
  getQualityConfigStatus: () => request<QualityConfigStatusResponse>("/quality/config-status"),
  getFailureSignatureTaxonomy: (days = 30, projectPath?: string) => {
    const params = new URLSearchParams({ days: String(days) });
    if (projectPath) params.set("project_path", projectPath);
    return request<FailureSignatureTaxonomyResponse>(
      `/quality/failure-signature-taxonomy?${params}`,
    );
  },
  listFalsePositives: (params: {
    page?: number;
    pageSize?: number;
    confirmed?: boolean | null;
    projectPath?: string;
  } = {}) => {
    const searchParams = new URLSearchParams({
      page: String(params.page ?? 1),
      page_size: String(params.pageSize ?? 25),
    });
    if (params.confirmed !== null && params.confirmed !== undefined) {
      searchParams.set("confirmed", String(params.confirmed));
    }
    if (params.projectPath) {
      searchParams.set("project_path", params.projectPath);
    }
    return request<FalsePositiveListResponse>(
      `/quality/false-positives?${searchParams}`,
    );
  },
  confirmFalsePositives: (ids: number[]) =>
    request<FalsePositiveConfirmResponse>("/quality/false-positives/confirm", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  dismissFalsePositives: (ids: number[]) =>
    request<FalsePositiveDismissResponse>("/quality/false-positives/dismiss", {
      method: "POST",
      body: JSON.stringify({ ids }),
    }),
  // Development
  getTicketAssessments: (
    page = 1,
    pageSize = 25,
    status?: string,
    includeUnassigned = false,
  ) => {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (status) params.set("status", status);
    if (includeUnassigned) params.set("include_unassigned", "true");
    return request<PaginatedResponse<TicketAssessmentResponse>>(`/development/tickets?${params}`);
  },
  getTicketAssessment: (id: number) =>
    request<TicketAssessmentDetailResponse>(`/development/tickets/${id}`),
  retryTicket: (id: number) =>
    request<{ status: string; jira_key: string }>(
      `/development/tickets/${id}/retry`,
      { method: "POST" },
    ),
  stopTicket: (id: number) =>
    request<{ status: string; jira_key: string }>(
      `/development/tickets/${id}/stop`,
      { method: "POST" },
    ),
  resumeTicket: (id: number) =>
    request<{ status: string; jira_key: string; ticket_status?: string }>(
      `/development/tickets/${id}/resume`,
      { method: "POST" },
    ),
  replayTicket: (id: number, stage: string) =>
    request<{ status: string; jira_key: string; stage: string }>(
      `/development/tickets/${id}/replay`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stage }),
      },
    ),
  resolveTicket: (id: number, resolution: string) =>
    request<{ status: string; jira_key: string; resolution: string }>(
      `/development/tickets/${id}/resolve`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resolution }),
      },
    ),
  updateTicketPriority: (id: number, priority: number) =>
    request<{ status: string; jira_key: string; priority: number }>(
      `/development/tickets/${id}/priority`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ priority }),
      },
    ),
  deleteTicket: (id: number) =>
    request<{ status: string; jira_key: string }>(
      `/development/tickets/${id}`,
      { method: "DELETE" },
    ),
  refreshTicket: (id: number) =>
    request<{ status: string; jira_key: string }>(
      `/development/tickets/${id}/refresh`,
      { method: "POST" },
    ),
  refreshAllTickets: () =>
    request<{ status: string; count: number }>(
      "/development/refresh-all",
      { method: "POST" },
    ),
  resetReopenCount: (id: number) =>
    request<{ status: string; jira_key: string; reopen_count: number }>(
      `/development/tickets/${id}/reset-reopen-count`,
      { method: "POST" },
    ),
  getTicketProgress: (id: number) =>
    request<TicketProgressResponse>(`/development/tickets/${id}/progress`),

  getPipelineFlow: (id: number) =>
    request<PipelineFlowResponse>(`/development/tickets/${id}/pipeline-flow`),
  pollDevelopmentTickets: () =>
    request<DevelopmentPollResponse>("/development/poll", { method: "POST" }),
  getDevelopmentConfig: () =>
    request<DevelopmentConfigResponse>("/development/config"),
  getDevelopmentStats: () => request<DevelopmentStatsResponse>("/development/stats"),
  getDevelopmentCosts: (days = 30) => {
    const params = new URLSearchParams({ days: String(days) });
    return request<DevelopmentCostResponse>(`/development/costs?${params}`);
  },
  getDevelopmentTrends: (months = 6) => {
    const params = new URLSearchParams({ months: String(months) });
    return request<DevelopmentTrendResponse>(`/development/trends?${params}`);
  },
  getEpicContexts: () =>
    request<EpicContextResponse[]>("/development/epics"),
  getEpicSiblings: (epicKey: string) =>
    request<EpicSiblingResponse[]>(`/development/epics/${epicKey}/siblings`),

  // Context push
  triggerContextPush: (projectPaths?: string[]) =>
    request<ContextPushTriggerResponse>("/context-push/trigger", {
      method: "POST",
      body: JSON.stringify(projectPaths ? { project_paths: projectPaths } : {}),
    }),
  pushGuidelineToRepo: (
    guidelineId: number,
    projectPath: string,
    updateId?: number,
  ) =>
    request<ContextPushGuidelineResponse>("/context-push/propose", {
      method: "POST",
      body: JSON.stringify({
        guideline_id: guidelineId,
        project_path: projectPath,
        update_id: updateId,
      }),
    }),
  getContextPushHistory: (opts?: { page?: number; pageSize?: number; projectPath?: string }) => {
    const params = new URLSearchParams({
      page: String(opts?.page ?? 1),
      page_size: String(opts?.pageSize ?? 25),
    });
    if (opts?.projectPath) params.set("project_path", opts.projectPath);
    return request<ContextPushHistoryResponse>(`/context-push/history?${params}`);
  },
  getContextPushPreview: () =>
    request<ContextPushPreviewResponse>("/context-push/preview"),

  // Slack diagnostics
  getSlackDiagnostics: () => request<SlackDiagnosticsResponse>("/slack/diagnostics"),
  getJiraDiagnostics: () => request<JiraDiagnosticsResponse>("/jira/diagnostics"),

  getDiagnostics: async (): Promise<DiagnosticsResponse> => {
    const token = getUser?.()?.access_token;
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(`${ROOT_URL}/diagnostics`, { headers });
    if (!response.ok) {
      throw new ApiError(response.status, statusMessage(response.status));
    }
    return response.json();
  },
};
